// Level-select UI shared by the Schnellspiel atlas and the Kampagne world map:
// the Squire/Knight/Lord rating row and the difficulty popup. Both screens hand
// the same registry payload to GameScene, so the gating rule (a difficulty
// opens once the one below it has been cleared) lives here once.

import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import type { MapDef } from "../maps/maps";
import { THEME, panel } from "./theme";

/** Draw a 3-icon Squire/Knight/Lord rating row: `cleared` of the 3 icons
 *  filled gold, the rest painted dim/empty. */
export function addStars(
  scene: Phaser.Scene,
  cx: number, cy: number,
  cleared: number,
  container: Phaser.GameObjects.Container,
  size: number, gap: number
): void {
  for (let i = 0; i < 3; i++) {
    const key = i < cleared ? "star-filled" : "star-empty";
    const star = scene.add.image(cx + (i - 1) * gap, cy, key).setDisplaySize(size, size);
    container.add(star);
  }
}

export const DIFFICULTIES = [
  { id: 1, label: "Squire", color: THEME.colors.gold,  needs: 0 },
  { id: 2, label: "Knight", color: THEME.colors.forge, needs: 1 },
  { id: 3, label: "Lord",   color: THEME.colors.blood, needs: 2 }
] as const;

export interface DifficultyPopupOptions {
  /** Highest difficulty already cleared on this map (0-3). */
  cleared: number;
  /** Fired with the chosen difficulty id. The caller owns the scene change,
   *  because the atlas sits over the menu while the world map replaces it. */
  onStart(map: MapDef, difficulty: number): void;
  onClose(): void;
}

/** Parchment popup with the three difficulty buttons. Returns the container so
 *  the caller can destroy it. */
export function openDifficultyPopup(
  scene: Phaser.Scene,
  map: MapDef,
  options: DifficultyPopupOptions
): Phaser.GameObjects.Container {
  const w = 380, h = 200;
  const x = (GAME_WIDTH - w) / 2;
  const y = (GAME_HEIGHT - h) / 2;

  const c = scene.add.container(0, 0).setDepth(THEME.depths.overlay);

  // Dim backdrop — clicking it closes the popup.
  const dim = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
    .setOrigin(0).setInteractive();
  dim.on("pointerdown", () => options.onClose());
  c.add(dim);

  c.add(panel(scene, x, y, w, h, "parchment"));

  c.add(scene.add.text(x + w / 2, y + 18, map.name, {
    fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.iron
  }).setOrigin(0.5, 0));

  c.add(scene.add.text(x + w / 2, y + 54, map.description, {
    fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.iron,
    wordWrap: { width: w - 40 }, align: "center"
  }).setOrigin(0.5, 0));

  addStars(scene, x + w / 2, y + h - 78, options.cleared, c, 22, 28);

  const btnW = (w - 60) / 3;
  const btnH = 34;
  const btnY = y + h - btnH - 14;
  DIFFICULTIES.forEach((d, i) => {
    const enabled = options.cleared >= d.needs;
    const bx = x + 18 + i * (btnW + 6);
    const bbg = panel(scene, bx, btnY, btnW, btnH, "iron");
    if (!enabled) bbg.setAlpha(0.5);
    const txt = scene.add.text(bx + btnW / 2, btnY + btnH / 2, d.label, {
      fontFamily: THEME.fonts.display, fontSize: "16px",
      color: enabled ? d.color : THEME.colors.lock,
      stroke: THEME.colors.ironDeep, strokeThickness: 2
    }).setOrigin(0.5);
    c.add(bbg); c.add(txt);
    if (enabled) {
      bbg.setInteractive({ useHandCursor: true });
      bbg.on("pointerover", () => { txt.setColor(THEME.colors.parchment); bbg.setTint(0xffeec0); });
      bbg.on("pointerout",  () => { txt.setColor(d.color); bbg.clearTint(); });
      bbg.on("pointerdown", () => options.onStart(map, d.id));
    }
  });

  // Close (X) button top-right of popup.
  const close = scene.add.text(x + w - 14, y + 8, "✕", {
    fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.iron
  }).setOrigin(1, 0).setInteractive({ useHandCursor: true });
  close.on("pointerover", () => close.setColor(THEME.colors.blood));
  close.on("pointerout",  () => close.setColor(THEME.colors.iron));
  close.on("pointerdown", () => options.onClose());
  c.add(close);

  return c;
}
